/**
 * Split parsed document text into readable blocks.
 *
 * Uploaded documents arrive as plain text — but that text is very often
 * markdown that was saved as `.txt`, so raw `##` markers, `[link](#anchor)`
 * syntax and `- ` bullets used to render literally on a customer's public
 * page. This parser understands both worlds:
 *
 *   - Markdown structure (`#` headings, bullet and numbered lists, links,
 *     inline code) is interpreted, never shown raw. Link syntax collapses to
 *     its text — a guide page has no working anchors, and a dead `#fragment`
 *     link is worse than plain words.
 *   - Plain text keeps the original heuristic: blank-line separated blocks
 *     become paragraphs, and a short single line with no terminal punctuation
 *     (or ending in "?") is treated as a heading. Deliberately conservative —
 *     a wrong heading is uglier than a missed one.
 *
 * A "Table of Contents" heading and the link list under it are dropped
 * entirely: the links cannot work here, and the page's own section structure
 * already is the table of contents.
 *
 * `**bold**` markers are left in place for the renderer's inline pass.
 */
const HEADING_MAX_CHARS = 100;

export interface GuideHeadingBlock {
  kind: "heading";
  /** 2 for top-level sections, 3 for sub-headings (`###` and deeper). */
  level: 2 | 3;
  value: string;
}

export interface GuideTextBlock {
  kind: "text";
  value: string;
}

export interface GuideListBlock {
  kind: "list";
  ordered: boolean;
  items: string[];
}

export type GuideBlock = GuideHeadingBlock | GuideTextBlock | GuideListBlock;

/** Collapse inline markdown that must never render raw. Keeps `**bold**`. */
const stripInlineMarkup = (text: string): string =>
  text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .trim();

/** A heading whose section is dropped wholesale (see doc comment). */
const TOC_HEADING = /^table of contents:?$/i;

const dropTableOfContents = (blocks: GuideBlock[]): GuideBlock[] => {
  const result: GuideBlock[] = [];
  for (let index = 0; index < blocks.length; index++) {
    const block = blocks[index];
    if (block.kind === "heading" && TOC_HEADING.test(block.value)) {
      while (index + 1 < blocks.length && blocks[index + 1].kind === "list") index++;
      continue;
    }
    result.push(block);
  }
  return result;
};

/**
 * @param detectPlainHeadings when false, only explicit markdown `#` headings
 * are treated as headings. Used for branded-document section bodies, where a
 * short answer line ("Yes — any time") must stay body text, exactly as the
 * PDF renders it.
 */
export const toBlocks = (
  content: string,
  detectPlainHeadings = true,
): GuideBlock[] => {
  const blocks: GuideBlock[] = [];
  let paragraph: string[] = [];
  let list: GuideListBlock | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const value = paragraph.join("\n").trim();
    paragraph = [];
    if (!value) return;
    const isHeading =
      detectPlainHeadings &&
      value.length <= HEADING_MAX_CHARS &&
      !value.includes("\n") &&
      (!/[.!?,;:]$/.test(value) || value.endsWith("?"));
    blocks.push(
      isHeading
        ? { kind: "heading", level: 2, value }
        : { kind: "text", value },
    );
  };

  const flushList = () => {
    if (list && list.items.length > 0) blocks.push(list);
    list = null;
  };

  for (const raw of content.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      flushList();
      flushParagraph();
      continue;
    }

    // Horizontal rules are markdown furniture, not content.
    if (/^[-=_*]{3,}$/.test(line)) {
      flushList();
      flushParagraph();
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushList();
      flushParagraph();
      const value = stripInlineMarkup(heading[2].replace(/#+\s*$/, ""));
      if (value) {
        blocks.push({
          kind: "heading",
          level: heading[1].length >= 3 ? 3 : 2,
          value,
        });
      }
      continue;
    }

    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = Boolean(numbered);
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { kind: "list", ordered, items: [] };
      const item = stripInlineMarkup((bullet ?? numbered)![1]);
      if (item) list.items.push(item);
      continue;
    }

    flushList();
    paragraph.push(stripInlineMarkup(line));
  }

  flushList();
  flushParagraph();

  return dropTableOfContents(blocks);
};
